/**
 * Hero (KMMO, mockup docs/art/mockups/hero.jpg): one equipment screen for the main hero.
 *   left    class crest, name, Lv + EXP, icon stat rows (hover explains), pentagon radar
 *   centre  the live VRM hero on the studio stage (a crest instead while a run is playing)
 *   gear    the 8 slots (2×4), tier set bar, Auto-equip
 *   bag     Gear / Gems / Materials icon tabs; Salvage (or Auto-socket / Fuse all) + ENHANCE
 * Replaces the Character, Inventory and Gems screens. Companions are auto-managed (no hero tabs);
 * the main hero is switched in the lobby. Click selects (ENHANCE targets it), double-click on a
 * bag item equips it, hover shows the item card with the stat change.
 */
import '../kmmo/kmmo.css';
import './hero.css';
import type { ClassDef, ClassId } from '@keystone/sim';
import { type CompiledCharacter, type Item, type ItemSlot, type OpError, MAX_HERO_LEVEL, equippedGear, itemOwner, itemScore, legendary, profileStanding, setBonusRows, setPieces, tierDef, tierSetFor, xpToNext } from '@keystone/progression';
import type { MetaScreenFactory } from '../context';
import { ROUTES } from '../router';
import { heroFor } from '../heroes';
import { SPEC_LEVEL, specInfo } from '@keystone/content';
import { el } from '../ui/dom';
import { SLOT_LABEL, itemName } from '../ui/itemText';
import { GEM_LABEL } from '../ui/glyphs';
import {
  type HeroStage,
  type ItemSlotHandle,
  type KmIcon,
  attachHoverCard,
  classCrest,
  divider,
  gearCard,
  hideHoverCard,
  iconTabs,
  iconUrl,
  itemSlot,
  kmModal,
  kmToast,
  mountHeroStage,
  panel,
  primaryButton,
  progressBar,
  radarChart,
  secondaryButton,
  slotIcon as slotGlyph,
  statList,
} from '../kmmo';
import { gemIcon, infoSheet, metaFrame } from '../kmmo/extra-hero';
import { profRows } from '../../hud/professions/profModel'; // +prof
import { professionsOf } from '@keystone/progression'; // +prof
import { compileFor, equipPreview, explainStat, opErrorMessage, sheetStats, type SheetStatKey, formatStat } from './characterModel';
import { RADAR_LABELS, autoEquipAll, bagGear, enhanceLevel, fuseAll, gemSlots, materials, radarAxes, salvageAll, salvageCandidates } from './heroModel';
import { gemsUnlockAt, gemsUnlocked } from './gemsModel';
import { RESOURCE_META, bundleEntries } from '../forge/resourceText';

type BagTab = 'gear' | 'gems' | 'materials';

/** Gear grid order (mockup): Helm Chest / Legs Gloves / Boots Weapon / Ring Trinket. */
const GEAR_ORDER: readonly ItemSlot[] = ['helm', 'chest', 'legs', 'gloves', 'boots', 'weapon', 'ring', 'trinket'];
const STAT_ICON: Readonly<Record<SheetStatKey, KmIcon>> = { ilvl: 'ilvl', power: 'power', hp: 'stamina', crit: 'crit', haste: 'haste', expertise: 'expertise', spirit: 'spirit' };
const MIN_BAG_CELLS = 20;

const RESOURCE_ICON_STEM: Readonly<Record<string, string>> = {
  fangsteel: 'forge_fangsteel', 'scale.ashen': 'forge_scale_ashen', 'scale.jade': 'forge_scale_jade', 'scale.gilded': 'forge_scale_gilded', 'scale.eternal': 'forge_scale_eternal',
  venom: 'forge_venom', moltskin: 'forge_moltskin', eyeFragments: 'forge_eye_fragment', sigils: 'forge_sigil', blessed: 'forge_blessed',
};

export const heroScreen: MetaScreenFactory = (ctx) => {
  const store = ctx.store;
  const want = ctx.params['tab'];
  let tab: BagTab = want === 'gems' || want === 'materials' ? want : 'gear';
  let selectedId: string | null = typeof ctx.params['itemId'] === 'string' ? (ctx.params['itemId'] as string) : null;
  let shownClass: ClassId = store.profile.mainClassId;
  const handles = new Map<string, ItemSlotHandle>();

  const f = metaFrame(ctx, { title: 'Hero', info: showInfo, className: 'kh-screen' });

  // Centre: the live hero (or the class crest while a run owns the GPU).
  let stage: HeroStage | null = null;
  const crestHost = el('div', 'kh-crest-stage', f.backdrop);
  if (!ctx.router.isOverlay) stage = mountHeroStage(f.backdrop, { heroes: [shownClass], centerX: 0.385, fill: 0.72, feetY: 0.89, ring: false });
  else classCrest(shownClass, { size: 'lg' }, crestHost);

  const left = panel({ className: 'kh-left' }, f.body);
  const gearP = panel({ className: 'kh-gear' }, f.body);
  const bagP = panel({ className: 'kh-bag' }, f.body);

  const main = (): ClassId => store.profile.mainClassId;
  const def = (): ClassDef | null => ctx.content.class(main()) ?? null;
  const abilityName = (id: string): string => ctx.content.ability(id)?.name ?? id;

  function render(): void {
    hideHoverCard();
    handles.clear();
    const p = store.profile;
    if (main() !== shownClass) {
      shownClass = main();
      void stage?.setHeroes([shownClass]);
      if (!stage) crestHost.replaceChildren(classCrest(shownClass, { size: 'lg' }));
    }
    if (selectedId && !p.inventory.some((i) => i.id === selectedId)) selectedId = null;
    const d = def();
    let compiled: CompiledCharacter | null = null;
    try {
      compiled = d ? compileFor(p, d) : null;
    } catch {
      compiled = null;
    }
    renderLeft(compiled);
    renderGear(d);
    renderBag(d);
  }

  // ------------------------------------------------------------------ left: identity + stats
  function renderLeft(c: CompiledCharacter | null): void {
    const p = store.profile;
    const id = main();
    const ch = p.characters[id]!;
    const hero = heroFor(id);
    left.body.replaceChildren();
    const who = el('div', 'kh-id', left.body);
    classCrest(id, { size: 'md' }, who);
    el('div', 'kh-name', who, hero.name);
    el('div', 'kh-class', who, def()?.name ?? hero.roleLabel);
    // +specs: the chosen specialisation (role and tagline), or where to get one.
    const spec = specInfo(ch.hero.specId ?? null);
    const sp = el('div', 'kh-spec', who, spec ? `${spec.name} · ${spec.role === 'tank' ? 'Tank' : spec.role === 'healer' ? 'Healer' : 'Damage'}` : ch.hero.level >= SPEC_LEVEL ? 'No specialisation: see Warden Ilsa' : `Specialisation at level ${SPEC_LEVEL}`);
    sp.title = spec ? `${spec.ui.tagline}\n${spec.ui.signature}` : 'At level 10 Warden Ilsa on the Tower Road runs the Advancement Trial: choose one of three specialisations.';
    if (spec) sp.style.cssText = `color:${spec.ui.glow};font-weight:700;font-size:12px;letter-spacing:0.04em;margin-top:2px;text-shadow:0 0 8px ${spec.ui.tint};`;
    else sp.style.cssText = 'color:#9aa3b5;font-size:11px;margin-top:2px;';

    const maxed = ch.hero.level >= MAX_HERO_LEVEL;
    const need = xpToNext(ch.hero.level);
    const pct = maxed ? 1 : Math.max(0, Math.min(1, ch.hero.xp / Math.max(1, need)));
    const lv = el('div', 'kh-lv km-num', left.body);
    el('span', '', lv, `Lv ${ch.hero.level}`);
    el('b', '', lv, maxed ? 'MAX' : `EXP ${(pct * 100).toFixed(1)}%`);
    progressBar({ value: pct * 100, max: 100, tone: 'blue', thin: true }, left.body);
    // +prof: the gathering professions (skill, bar, next unlock).
    if (p.character) {
      const prof = el('div', 'kh-prof', left.body);
      prof.style.cssText = 'display:flex;flex-direction:column;gap:3px;margin:6px 0 2px;';
      for (const r of profRows(professionsOf(p.character).skills, professionsOf(p.character).byKind)) {
        const row = el('div', 'kh-prof-row', prof);
        row.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;color:#d8dce4;';
        el('span', '', row, r.name);
        el('b', 'km-num', row, `${r.skill} / ${r.cap}`);
        row.title = `${r.next}. Gather herbs and ore in the world (L opens Professions).`;
        progressBar({ value: r.pct * 100, max: 100, tone: r.prof === 'mining' ? 'gold' : 'green', thin: true }, prof);
      }
    }

    if (!c) return;
    const s = sheetStats(c);
    const keys: SheetStatKey[] = ['ilvl', 'power', 'hp', 'crit', 'haste', 'expertise'];
    if (heroFor(id).roleLabel === 'Healer' || s.spirit > 0) keys.push('spirit');
    const dl = statList(
      keys.map((k) => ({ icon: STAT_ICON[k], label: k === 'hp' ? 'Health' : k === 'ilvl' ? 'Item Level' : explainStat(k, s, id).title, value: formatStat(k, s[k]) })),
      left.body,
    );
    dl.classList.toggle('kh-stats-7', keys.length > 6);
    [...dl.children].forEach((row, i) => {
      const k = keys[i]!;
      (row as HTMLElement).tabIndex = 0;
      attachHoverCard(row as HTMLElement, () => {
        const e = explainStat(k, s, id, abilityName);
        const card = el('div', 'km-card kh-stat-tip');
        const h = el('div', 'kh-stat-tip-head', card);
        el('span', '', h, e.title);
        el('span', 'km-num km-t-gold', h, e.value);
        for (const l of e.lines) el('p', 'km-card-effect', card, l);
        return card;
      });
    });
    divider(left.body);
    const cap = tierDef(profileStanding(p).tier).maxIlvl;
    radarChart(RADAR_LABELS.map((label, i) => ({ label, value: radarAxes(c, cap)[i] ?? 0 })), {}, left.body);
  }

  // ------------------------------------------------------------------ gear: 2×4 + set + auto-equip
  function renderGear(d: ClassDef | null): void {
    const p = store.profile;
    const id = main();
    const gear = equippedGear(p, id);
    const pieces = setPieces(gear, id);
    const bag = bagGear(p, id);
    gearP.body.replaceChildren();
    const grid = el('div', 'kh-gear-grid', gearP.body);
    for (const slot of GEAR_ORDER) {
      const item = gear[slot] ?? null;
      const cell = el('div', 'kh-gear-cell', grid);
      el('span', 'kh-gear-label', cell, SLOT_LABEL[slot]);
      const upgrade = bag.some((b) => b.slot === slot && (!item || betterThan(b, item)));
      const h = itemSlotFor(item, { slot, equipped: !!item, isNew: upgrade, title: item ? undefined : `${SLOT_LABEL[slot]}: empty` }, cell);
      if (item) attachHoverCard(h.slot, () => gearCard(item, { equippedSetCount: pieces, hint: 'Click to select · ENHANCE to upgrade' }));
    }
    const set = tierSetFor(id);
    if (set) {
      const bar = el('div', `kh-set${pieces >= 4 ? ' full' : ''}`, gearP.body);
      el('span', '', bar, 'Tier Set');
      el('b', 'km-num', bar, `${pieces}/5`);
      bar.title = set.name;
      bar.tabIndex = 0;
      attachHoverCard(bar, () => {
        const card = el('div', 'km-card kh-set-tip');
        el('div', 'km-card-set-name', card, `${set.name}  ${pieces}/5`);
        for (const r of d ? setBonusRows(d) : []) el('div', `km-card-set-bonus${pieces >= r.pieces ? ' on' : ''}`, card, `(${r.pieces}) ${r.text.name}: ${r.text.description}`);
        return card;
      });
    }
    const can = autoEquipAll(p, id).equipped;
    secondaryButton('Auto-equip', {
      block: true,
      disabled: can === 0,
      title: can === 0 ? 'Your best gear is already equipped' : `Equip ${can} better item${can === 1 ? '' : 's'}`,
      className: 'kh-autoequip',
      onClick: () => {
        if (!autoEquipAll(store.profile, main()).equipped) return void kmToast('Your best gear is already equipped');
        void store.act('autoEquip', { classId: main() }).then((r) => {
          if (!r.ok) kmToast(opErrorMessage(r.reason as OpError), 'error');
          else kmToast(`Equipped ${r.equipped} item${r.equipped === 1 ? '' : 's'}`, 'success');
        });
      },
    }, gearP.body);
  }

  /** `a` would replace `b` on the main hero (same rule as Auto-equip). */
  const betterThan = (a: Item, b: Item): boolean => {
    if (a.legendaryId && legendary(a.legendaryId)?.classId !== main()) return false;
    return itemScore(a, main()) > itemScore(b, main());
  };
  const equipPreviewSafe = (item: Item): ReturnType<typeof equipPreview> => {
    const d = def();
    if (!d) return null;
    if (item.legendaryId && itemOwner(store.profile, item.id)) return null;
    return equipPreview(store.profile, d, item);
  };

  function itemSlotFor(item: Item | null, o: { slot?: ItemSlot; equipped?: boolean; isNew?: boolean; title?: string }, parent: HTMLElement): ItemSlotHandle {
    if (!item) return itemSlot({ empty: true, icon: o.slot ? slotGlyph(o.slot) : undefined, title: o.title }, parent);
    const h = itemSlot({
      rarity: item.rarity,
      icon: slotGlyph(item.slot),
      plus: enhanceLevel(item),
      setPiece: !!item.setId,
      equipped: o.equipped,
      isNew: o.isNew,
      selected: item.id === selectedId,
      onClick: () => select(item.id),
    }, parent);
    handles.set(item.id, h);
    return h;
  }

  function select(id: string): void {
    selectedId = selectedId === id ? null : id;
    for (const [k, h] of handles) h.setSelected(k === selectedId);
  }

  // ------------------------------------------------------------------ bag
  function renderBag(d: ClassDef | null): void {
    const p = store.profile;
    const id = main();
    bagP.body.replaceChildren();
    const bag = bagGear(p, id);
    const anyUpgrade = bag.some((b) => {
      const cur = equippedGear(p, id)[b.slot];
      return !cur || betterThan(b, cur);
    });
    iconTabs(
      [
        { id: 'gear', icon: 'helm', title: 'Gear', dot: anyUpgrade },
        { id: 'gems', icon: 'gem', title: gemsUnlocked(p) ? 'Gems' : `Gems (unlock at ${gemsUnlockAt().label})` },
        { id: 'materials', icon: 'leaf', title: 'Materials' },
      ],
      { active: tab, onChange: (t) => { tab = t as BagTab; renderBag(def()); } },
      bagP.body,
    );
    const grid = el('div', 'kh-bag-grid', bagP.body);
    const foot = el('div', 'kh-bag-foot', bagP.body);
    if (tab === 'gear') bagGearTab(grid, foot, bag, d);
    else if (tab === 'gems') bagGemsTab(grid, foot);
    else bagMaterialsTab(grid, foot);
    primaryButton('Enhance', { onClick: openEnhance, className: 'kh-enhance' }, foot);
  }

  function bagGearTab(grid: HTMLElement, foot: HTMLElement, bag: Item[], d: ClassDef | null): void {
    const id = main();
    for (const item of bag) {
      const cur = equippedGear(store.profile, id)[item.slot];
      const h = itemSlotFor(item, { isNew: !cur || betterThan(item, cur) }, grid);
      h.slot.addEventListener('dblclick', () => equip(item));
      attachHoverCard(h.slot, () => bagCard(item, d));
    }
    padGrid(grid, bag.length);
    el('span', 'kh-count km-num', foot, bag.length ? `Bag ${bag.length}` : '').title = 'Items in your bag';
    const cands = salvageCandidates(store.profile);
    secondaryButton('Salvage', {
      disabled: cands.length === 0,
      title: cands.length ? `Salvage ${cands.length} item${cands.length === 1 ? '' : 's'} nobody needs` : 'Nothing to salvage: every bag item is someone’s upgrade',
      onClick: () => confirmSalvage(),
    }, foot);
  }

  function bagCard(item: Item, d: ClassDef | null): HTMLElement {
    const card = gearCard(item, { hint: 'Double-click to equip' });
    const deltas = d ? equipPreviewSafe(item) : null;
    const changed = (deltas ?? []).filter((x) => x.tone !== 'same' && x.key !== 'hp').slice(0, 4);
    if (changed.length) {
      const box = el('div', 'kh-cmp');
      for (const x of changed) {
        const r = el('div', `kh-cmp-row ${x.tone}`, box);
        el('span', '', r, x.label);
        el('b', 'km-num', r, x.text);
      }
      card.insertBefore(box, card.querySelector('.km-card-hint'));
    }
    return card;
  }

  function equip(item: Item): void {
    void store.act('equip', { classId: main(), itemId: item.id }).then((r) => {
      if (!r.ok) kmToast(opErrorMessage(r.reason as OpError), 'error');
      else kmToast(`Equipped ${itemName(item)}`, 'success');
    });
  }

  function bagGemsTab(grid: HTMLElement, foot: HTMLElement): void {
    const p = store.profile;
    if (!gemsUnlocked(p)) {
      grid.classList.add('kh-locked');
      itemSlot({ rarity: 'epic', icon: gemIcon('sapphire', 2), locked: true, size: 'lg', caption: gemsUnlockAt().label }, grid);
      el('span', 'kh-count', foot, '');
      return;
    }
    const gems = gemSlots(p);
    for (const g of gems) {
      const h = itemSlot({ rarity: (['common', 'uncommon', 'rare', 'epic', 'legendary'] as const)[Math.min(4, g.gem.rank - 1)], icon: gemIcon(g.gem.color, g.gem.rank), count: g.count, plus: undefined }, grid);
      attachHoverCard(h.slot, () => `${GEM_LABEL[g.gem.color]} · rank ${g.gem.rank} × ${g.count}`);
    }
    padGrid(grid, gems.length);
    el('span', 'kh-count km-num', foot, String(gems.reduce((a, g) => a + g.count, 0))).title = 'Gems in your stash';
    secondaryButton('Auto-socket', {
      title: 'Fill your sockets with the best gems for your role',
      onClick: () => {
        void store.act('autoGem', { classId: main() }).then((r) => kmToast(r.ok ? 'Gems socketed' : opErrorMessage(r.reason as OpError), r.ok ? 'success' : 'error'));
      },
    }, foot);
    secondaryButton('Fuse all', {
      title: 'Fuse every 3 matching gems into 1 of the next rank (gold)',
      onClick: () => {
        if (!fuseAll(store.profile).fused) return void kmToast('Nothing to fuse');
        void store.act('fuseAll', {}).then((r) => {
          if (r.ok) kmToast(`Fused ${r.fused} gem${r.fused === 1 ? '' : 's'}`, 'success');
        });
      },
    }, foot);
  }

  function bagMaterialsTab(grid: HTMLElement, foot: HTMLElement): void {
    const mats = materials(store.profile);
    for (const m of mats) {
      const h = itemSlot({ rarity: m.rarity, icon: iconUrl(m.icon, true), count: m.count, dim: m.count <= 0 }, grid);
      h.slot.tabIndex = 0;
      attachHoverCard(h.slot, () => {
        const card = el('div', 'km-card kh-stat-tip');
        const head = el('div', 'kh-stat-tip-head', card);
        el('span', '', head, m.label);
        el('span', 'km-num km-t-gold', head, String(m.count));
        el('p', 'km-card-effect', card, m.use);
        return card;
      });
    }
    padGrid(grid, mats.length);
    el('span', 'kh-count', foot, '');
  }

  function padGrid(grid: HTMLElement, n: number): void {
    for (let i = n; i < MIN_BAG_CELLS || i % 5; i++) itemSlot({ empty: true }, grid);
  }

  function confirmSalvage(): void {
    const ids = salvageCandidates(store.profile).map((i) => i.id);
    if (!ids.length) return;
    const preview = salvageAll(store.profile, ids);
    const m = kmModal('Salvage');
    el('p', 'km-modal-text', m.body, `Salvage ${preview.count} item${preview.count === 1 ? '' : 's'} that no hero needs?`);
    const row = el('div', 'kh-yield', m.body);
    for (const e of bundleEntries(preview.gained).slice(0, 5)) {
      const stem = RESOURCE_ICON_STEM[e.key];
      itemSlot({ rarity: 'rare', icon: stem ? iconUrl(stem, true) : 'chest', count: e.n, caption: RESOURCE_META[e.key].short, size: 'sm' }, row);
    }
    secondaryButton('Cancel', { onClick: () => m.close() }, m.footer);
    primaryButton('Salvage', {
      onClick: () => {
        m.close();
        void store.act('salvageAll', { ids }).then((r) => {
          if (!r.ok || !r.count) return void kmToast('Nothing salvaged', 'error');
          kmToast(`Salvaged ${r.count} item${r.count === 1 ? '' : 's'}`, 'success');
        });
      },
    }, m.footer);
  }

  function openEnhance(): void {
    const p = store.profile;
    const gear = equippedGear(p, main());
    const itemId = selectedId ?? gear.weapon?.id ?? Object.values(gear).find(Boolean)?.id;
    ctx.router.open(ROUTES.forge, { tab: 'enhance', ...(itemId ? { itemId } : {}) });
  }

  function showInfo(): void {
    infoSheet('Hero', [
      'Your hero, gear and bag. Companions manage their own gear after every run.',
      '+N is how many times an item was enhanced. E marks equipped items, and the gold diamond marks a tier set piece. A red dot means an upgrade is waiting in your bag.',
      'Click an item to select it for ENHANCE. Double-click a bag item to equip it. Auto-equip wears every upgrade at once.',
      'Salvage breaks down bag items that no hero needs and returns Fangsteel and Scales.',
      'Gems add Gem Power per colour across your gear; bonuses unlock at 120, 240, 360 and 480.',
      'The chart compares your hero to gear at your rank’s item level cap.',
    ]);
  }

  render();
  const unsub = store.subscribe(() => render());
  return {
    el: f.el,
    dispose() {
      unsub();
      f.dispose();
      hideHoverCard();
      stage?.dispose();
      stage = null;
    },
  };
};

